// src/components/ReporterDetailsPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getApiBaseUrl } from '../config/api';

interface ReportRecord {
  _id: string;
  ItemID?: string;
  CommentID?: string;
  UserID?: string;
  Report?: string;
  ItemType?: string;
  ReporterUserID?: string;
  Reviewed?: boolean;
  ReviewedAt?: string | null;
}

const ReporterDetailsPage: React.FC = () => {
  const { reportId, reporterUserId } = useParams<{
    reportId: string;
    reporterUserId: string;
  }>();
  const navigate = useNavigate();

  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // fixed columns in desired order
  const columns = [
    { key: '_id', label: 'ReportID' },
    { key: 'ItemType', label: 'Item Type' },
    { key: 'ReporterUserID', label: 'Reporter User ID', isWide: true },
    { key: 'Report', label: 'Report', isWide: true },
    { key: 'ItemID', label: 'Item ID' },
    { key: 'CommentID', label: 'Comment ID' },
    { key: 'UserID', label: 'User ID' },
    { key: 'Reviewed', label: 'Reviewed' },
  ];

  useEffect(() => {
    if (!reportId || !reporterUserId) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      navigate(-1);
      return;
    }

    const fetchReports = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${getApiBaseUrl()}/video/t/findAllReports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            ReporterUserID: true,
            ReportID: reportId,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load reports');
        setReports(json.data || []);
      } catch (err: any) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [reportId, reporterUserId, navigate]);

  if (loading) return <p className='text-center mt-10'>Loading…</p>;

  return (
    <>
      <Navbar />
      <div className='min-h-screen bg-gray-100 p-6'>
        <button onClick={() => navigate(-1)} className='mb-4 text-blue-600 hover:underline'>
          &larr; Back
        </button>
        <h3 className='text-xl font-bold mb-4'>All Reports By USER {reporterUserId}</h3>

        {reports.length === 0 ? (
          <p className='text-gray-500'>No reports found.</p>
        ) : (
          <div>
            <table className='w-full table-fixed divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {reports.map((r) => (
                  <tr key={r._id}>
                    {columns.map((col) => {
                      // format ReviewedAt into a human date
                      let value = (r as any)[col.key];
                      if (col.key === 'ReviewedAt') {
                        value = value ? new Date(value).toLocaleString() : '';
                      }
                      return (
                        <td key={col.key} className='px-4 py-2 text-sm text-gray-700 whitespace-nowrap'>
                          {col.isWide ? (
                            // only the wide column scrolls inside itself
                            <div className='overflow-x-auto whitespace-nowrap'>{String(value ?? '')}</div>
                          ) : (
                            // other columns just truncate overflow
                            <div className='truncate'>{String(value ?? '')}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ReporterDetailsPage;
